/**
 * Corrective Action Report Tab (Step 3) for NCR Edit
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Save, Send } from 'lucide-react';
import { NCREditFormData } from './types';
import { useFormatters } from '../../contexts/PreferencesContext';

interface Step3TabProps {
  formData: NCREditFormData;
  canEdit: boolean;
  saving: boolean;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitCAR: () => void;
}

export function CorrectiveActionTab({
  formData,
  canEdit,
  saving,
  onUpdateFormData,
  onSave,
  onSubmitCAR,
}: Step3TabProps) {
  const { formatDate } = useFormatters();
  return (
      <Card>
        <CardHeader>
          <CardTitle>Corrective Action Report (Supplier Only)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Lead Time Tracking */}
            {(formData.ca_required_date ||
              formData.car_required_date ||
              formData.ca_submitted_date ||
              formData.car_submitted_date) && (
              <div className="bg-muted/50 p-4 rounded-lg border">
                <h4 className="text-sm font-semibold mb-3">
                  Lead Time Tracking
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {formData.ca_required_date && (
                    <div>
                      <Label className="text-xs text-muted-foreground">CA Required Date</Label>
                      <p className="font-medium text-foreground">
                        {formatDate(formData.ca_required_date as string)}
                      </p>
                    </div>
                  )}
                  {formData.ca_submitted_date && (
                    <div>
                      <Label className="text-xs text-muted-foreground">CA Submitted Date</Label>
                      <p className="font-medium text-foreground">
                        {formatDate(formData.ca_submitted_date as string)}
                      </p>
                    </div>
                  )}
                  {formData.car_required_date && (
                    <div>
                      <Label className="text-xs text-muted-foreground">CAR Required Date</Label>
                      <p className="font-medium text-foreground">
                        {formatDate(formData.car_required_date as string)}
                      </p>
                    </div>
                  )}
                  {formData.car_submitted_date && (
                    <div>
                      <Label className="text-xs text-muted-foreground">CAR Submitted Date</Label>
                      <p className="font-medium text-foreground">
                        {formatDate(formData.car_submitted_date as string)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <Label>Corrective Action *</Label>
              <Textarea
                value={formData.corrective_action || ''}
                onChange={(e) => onUpdateFormData('corrective_action', e.target.value)}
                disabled={!canEdit}
                rows={4}
              />
            </div>

            <div>
              <Label>Preventive Action *</Label>
              <Textarea
                value={formData.preventive_action || ''}
                onChange={(e) => onUpdateFormData('preventive_action', e.target.value)}
                disabled={!canEdit}
                rows={4}
              />
            </div>

            <div>
              <Label>Impact Assessment *</Label>
              <Textarea
                value={formData.impact_assessment || ''}
                onChange={(e) => onUpdateFormData('impact_assessment', e.target.value)}
                disabled={!canEdit}
                rows={3}
                maxLength={500}
              />
            </div>

            {/* 8D Step 6-7: Impact on Similar Products */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6-D7: Impact on Similar Products</h4>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.similar_products_affected || false}
                  onChange={(e) => onUpdateFormData('similar_products_affected', e.target.checked)}
                  disabled={!canEdit}
                />
                <Label>Similar products/processes affected by this issue?</Label>
              </div>

              {formData.similar_products_affected && (
                <div>
                  <Label>Impact Analysis on Similar Products</Label>
                  <Textarea
                    value={formData.impact_on_similar_products || ''}
                    onChange={(e) => onUpdateFormData('impact_on_similar_products', e.target.value)}
                    disabled={!canEdit}
                    rows={3}
                    placeholder="Describe which similar products/processes are affected and what actions have been taken..."
                  />
                </div>
              )}
            </div>

            {/* 8D Step 6: Training Requirements */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6: Training Requirements</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.training_required || false}
                  onChange={(e) => onUpdateFormData('training_required', e.target.checked)}
                  disabled={!canEdit}
                />
                <Label>Training Required</Label>
              </div>
            </div>

            {formData.training_required && (
              <div>
                <Label>Training Details</Label>
                <Textarea
                  value={formData.training_details || ''}
                  onChange={(e) => onUpdateFormData('training_details', e.target.value)}
                  disabled={!canEdit}
                  rows={2}
                />
              </div>
            )}

            {/* 8D Step 6: Documentation Updates */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6: Documentation Updates</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_process_flow_updated || false}
                    onChange={(e) => onUpdateFormData('doc_process_flow_updated', e.target.checked)}
                    disabled={!canEdit}
                  />
                  <Label>Process Flow Diagram Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_control_plan_updated || false}
                    onChange={(e) => onUpdateFormData('doc_control_plan_updated', e.target.checked)}
                    disabled={!canEdit}
                  />
                  <Label>Control Plan Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_fmea_updated || false}
                    onChange={(e) => onUpdateFormData('doc_fmea_updated', e.target.checked)}
                    disabled={!canEdit}
                  />
                  <Label>FMEA (Failure Mode Effects Analysis) Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_work_instructions_updated || false}
                    onChange={(e) => onUpdateFormData('doc_work_instructions_updated', e.target.checked)}
                    disabled={!canEdit}
                  />
                  <Label>Work Instructions Updated</Label>
                </div>
                <div className="mt-3">
                  <Label>Other Documentation Updated (specify)</Label>
                  <Textarea
                    value={formData.doc_other_updated || ''}
                    onChange={(e) => onUpdateFormData('doc_other_updated', e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    placeholder="Specify any other documentation that was updated..."
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Documentation References</Label>
              <Textarea
                value={formData.documentation_references || ''}
                onChange={(e) => onUpdateFormData('documentation_references', e.target.value)}
                disabled={!canEdit}
                rows={2}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.corrective_action_notes || ''}
                onChange={(e) => onUpdateFormData('corrective_action_notes', e.target.value)}
                disabled={!canEdit}
                rows={2}
                maxLength={500}
              />
            </div>

            {canEdit && (
              <div className="flex gap-2">
                <Button onClick={onSave} disabled={saving} variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button onClick={onSubmitCAR} disabled={saving}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit CAR
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
  );
}
